import React, { useState, useRef } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';

const DIRECTIONS = [
  { val:'', label:'All Directions' },
  { val:'N', label:'North' },
  { val:'S', label:'South' },
  { val:'E', label:'East' },
  { val:'W', label:'West' },
];
const STAGES = ['','BOX','PART','GANGSAW','GENERAL'];
const SHIFTS = ['','MORNING','AFTERNOON','NIGHT','FULL'];
const DIR_FULL = {N:'North',S:'South',E:'East',W:'West'};

const statusColor = s => s==='COMPLETED'?'#3B6D11':s==='ON_HOLD'?'#854F0B':'#185FA5';
const statusBg    = s => s==='COMPLETED'?'#EAF3DE':s==='ON_HOLD'?'#FAEEDA':'#E6F1FB';
const formatDate  = d => { try{ return new Date(d).toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long',year:'numeric'}); }catch{ return d; } };

// ─── Company Header ───────────────────────────────────────────────────────────
const CompanyHeader = ({ reportTitle, reportDate, reportNumber }) => (
  <div style={{ borderBottom:'2px solid #1e3a5f', paddingBottom:6, marginBottom:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
      <img src={`${window.location.origin}/TycheAbodes-logo1.png`} alt="Logo" style={{ width:50, height:50, objectFit:'contain' }} onError={e=>e.target.style.display='none'}/>
      <div>
        <div style={{ fontSize:13, fontWeight:800, color:'#1e3a5f', fontFamily:'Inter,sans-serif' }}>TYCHE ABODES PRIVATE LIMITED</div>
        <div style={{ fontSize:10, color:'#555' }}>No. 401, Ramani's Frazer Square, Goods Shed Road, Coimbatore - 641001 | 📞 +91 94433 23246 | GSTIN: 33AAKCT5967G1ZY</div>
      </div>
    </div>
    <div style={{ textAlign:'right' }}>
      <div style={{ fontSize:14, fontWeight:800, color:'#1e3a5f', textTransform:'uppercase' }}>{reportTitle}</div>
      <div style={{ fontSize:11, color:'#555' }}>No: <strong>{reportNumber}</strong> | Date: <strong>{reportDate}</strong></div>
      <div style={{ fontSize:11, color:'#555' }}>Generated: <strong>{new Date().toLocaleDateString('en-IN')}</strong></div>
    </div>
  </div>
);

// ─── Supervisor Box ───────────────────────────────────────────────────────────
const SupervisorBox = ({ session }) => (
  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:0, border:'1px solid #ddd', borderRadius:6, marginBottom:14 }}>
    {[
      ['Supervisor', session.supervisor_name || 'S Sivakumar'],
      ['Contact', '+91 97913 38573'],
      ['Shift', session.shift || '—'],
      ['Direction', DIR_FULL[session.direction] || session.direction || '—'],
      ['Stage', session.production_stage || '—'],
      ['Block Reference', session.production_stage==='GENERAL' ? '—' : session.block_reference || '—'],
    ].map(([l,v],i)=>(
      <div key={i} style={{ padding:'8px 12px', borderRight:i%3<2?'1px solid #ddd':'none', borderBottom:i<3?'1px solid #ddd':'none', background:i%2===0?'#f8f9fc':'#fff' }}>
        <div style={{ fontSize:9, color:'#888', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:2 }}>{l}</div>
        <div style={{ fontSize:12, fontWeight:600, color:'#1e3a5f' }}>{v}</div>
      </div>
    ))}
  </div>
);

const calcRate = (entry) => {
  const d = entry.data || {};
  const wt = entry.work_type;
  const results = [];
  if(wt==='DRILLING'){
    (d.selected_machines||[]).forEach(m=>{
      const md=(d.machine_data||{})[m]||{};
      const hrs=((parseFloat(md.running_hours||0)*60)+parseFloat(md.running_minutes||0))/60;
      const totalFt=(md.holes||[]).reduce((s,h)=>s+(parseFloat(h.running_feet)||0),0);
      if(hrs>0&&totalFt>0) results.push({ machine:m, hrs, output:totalFt.toFixed(2), unit:'ft', rate:(totalFt/hrs).toFixed(2) });
    });
  }
  if(wt==='WIRE_SAW'){
    (d.selected_machines||[]).forEach(m=>{
      const md=(d.machine_data||{})[m]||{};
      const hrs=((parseFloat(md.running_hours||0)*60)+parseFloat(md.running_minutes||0))/60;
      const area=parseFloat(md.cutting_area||0);
      if(hrs>0&&area>0) results.push({ machine:m, hrs, output:area.toFixed(2), unit:'Sq.m', rate:(area/hrs).toFixed(2) });
    });
  }
  if(wt==='TRANSPORT'){
    const trips=parseFloat(d.trips||0);
    results.push({ machine:d.vehicle_type||'Vehicle', hrs:null, output:trips, unit:'trips', rate:null });
  }
  if(wt==='BLADE_CUTTING'){
    results.push({ machine:'Blade Cutter', hrs:parseFloat(d.running_hours||0), output:d.number_of_frames||0, unit:'frames', rate:null });
  }
  return results;
};

// ─── Work Entry Detail ────────────────────────────────────────────────────────
const WorkEntryDetail = ({ entry, idx }) => {
  const d = entry.data || {};
  const wt = entry.work_type;
  const wtLabel = { DRILLING:'Drilling', WIRE_SAW:'Wire Saw', BLADE_CUTTING:'Blade Cutting', BLASTING:'Blasting', OFFSET:'Offset Work', WASTE:'Waste Management', TRANSPORT:'Transport', OTHER:'Other Work' };

  const Row = ({label, val, mono, bold, color}) => (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'3px 0', borderBottom:'0.5px solid #f0f0f0' }}>
      <span style={{ fontSize:11, color:'#666', minWidth:130 }}>{label}</span>
      <span style={{ fontSize:11, fontFamily:mono?'DM Mono,monospace':'inherit', fontWeight:bold?700:500, color:color||'#1e3a5f' }}>{val}</span>
    </div>
  );

  const SectionTitle = ({children}) => (
    <div style={{ fontSize:9, fontWeight:700, color:'#1e3a5f', textTransform:'uppercase', letterSpacing:'1px', borderBottom:'1px solid #1e3a5f', paddingBottom:3, marginBottom:6, marginTop:10 }}>{children}</div>
  );

  const renderDrilling = () => (
    <div>
      {(d.selected_machines||[]).map((machine, mi) => {
        const md = (d.machine_data||{})[machine]||{};
        const totalFt = (md.holes||[]).reduce((s,h)=>s+(parseFloat(h.running_feet)||0),0);
        const hrs = ((parseFloat(md.running_hours||0)*60)+parseFloat(md.running_minutes||0))/60;
        return (
          <div key={mi} style={{ marginBottom:10, padding:10, background:'#f8f9fc', borderRadius:6, border:'1px solid #e8eaf0' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#1e3a5f', marginBottom:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span>🔩 {machine}</span>
              {hrs>0&&totalFt>0&&<span style={{ fontSize:11, background:'#1e3a5f', color:'#fff', padding:'2px 10px', borderRadius:20 }}>{(totalFt/hrs).toFixed(2)} ft/hr</span>}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:12 }}>
              <div>
                <SectionTitle>Machine</SectionTitle>
                {md.running_hours&&<Row label="Running Hours" val={`${md.running_hours}h ${md.running_minutes||0}m`} mono/>}
                {md.engine_hours_start&&<Row label="Engine Start" val={md.engine_hours_start} mono/>}
                {md.engine_hours_end&&<Row label="Engine End" val={md.engine_hours_end} mono/>}
                {md.fuel_consumed&&<Row label="Fuel Consumed" val={`${md.fuel_consumed} L`} mono color="#854F0B"/>}
                {md.operator?.operator_name&&<Row label="Operator" val={md.operator.operator_name} bold/>}
                {md.operator?.operator_role&&<Row label="Role" val={md.operator.operator_role}/>}
              </div>
              <div>
                <SectionTitle>Drill Bit</SectionTitle>
                {md.drill_bit_given&&<>
                  <Row label="Given (inches)" val={`${md.drill_bit_given}"`} mono/>
                  <Row label="Taken Back" val={`${md.drill_bit_taken||0}"`} mono/>
                  <Row label="Used (Net)" val={`${(parseFloat(md.drill_bit_given||0)-parseFloat(md.drill_bit_taken||0)).toFixed(3)}"`} mono bold color="#185FA5"/>
                </>}
                {md.big_drill_bit_given&&<>
                  <Row label="Big Bit Given" val={`${md.big_drill_bit_given}"`} mono/>
                  <Row label="Big Bit Taken" val={`${md.big_drill_bit_taken||0}"`} mono/>
                  <Row label="Big Bit Used" val={`${(parseFloat(md.big_drill_bit_given||0)-parseFloat(md.big_drill_bit_taken||0)).toFixed(3)}"`} mono bold color="#854F0B"/>
                </>}
                {!md.drill_bit_given&&!md.big_drill_bit_given&&<div style={{fontSize:11,color:'#999'}}>No drill bit data</div>}
              </div>
              <div>
                {md.compressor?.operation_type&&<>
                  <SectionTitle>Compressor</SectionTitle>
                  <Row label="Operation" val={md.compressor.operation_type}/>
                  {md.compressor.fuel&&<Row label="Fuel" val={`${md.compressor.fuel} L`} mono color="#854F0B"/>}
                  {md.compressor.points_used&&<Row label="Points Used" val={md.compressor.points_used} mono/>}
                  {md.compressor.pressure_level&&<Row label="Pressure" val={`${md.compressor.pressure_level} PSI`} mono/>}
                  {md.compressor.eb_start&&<>
                    <Row label="EB Start" val={md.compressor.eb_start} mono/>
                    <Row label="EB End" val={md.compressor.eb_end} mono/>
                    <Row label="Total Units" val={`${(parseFloat(md.compressor.eb_end||0)-parseFloat(md.compressor.eb_start||0)).toFixed(2)}`} mono bold color="#185FA5"/>
                  </>}
                </>}
              </div>
            </div>
            {md.holes?.length>0&&(
              <div style={{ marginTop:10 }}>
                <SectionTitle>Hole Details — Total: {totalFt.toFixed(2)} ft</SectionTitle>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {md.holes.map((h,hi)=>(
                    <div key={hi} style={{ background:'#E6F1FB', border:'1px solid #B5D4F4', borderRadius:4, padding:'3px 10px', fontSize:11 }}>
                      {h.hole}: <strong>{h.running_feet} ft</strong>{h.diameter&&` ∅${h.diameter}mm`}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderWireSaw = () => (
    <div>
      {(d.selected_machines||[]).map((machine, mi) => {
        const md=(d.machine_data||{})[machine]||{};
        const hrs=((parseFloat(md.running_hours||0)*60)+parseFloat(md.running_minutes||0))/60;
        const area=parseFloat(md.cutting_area||0);
        return (
          <div key={mi} style={{ marginBottom:10, padding:10, background:'#f8f9fc', borderRadius:6, border:'1px solid #e8eaf0' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#185FA5', marginBottom:8, display:'flex', justifyContent:'space-between' }}>
              <span>🔪 Wire Saw — {machine} | {d.cutting_type}</span>
              {hrs>0&&area>0&&<span style={{ fontSize:11, background:'#185FA5', color:'#fff', padding:'2px 10px', borderRadius:20 }}>{(area/hrs).toFixed(2)} Sq.m/hr</span>}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:12 }}>
              <div>
                <SectionTitle>Work Details</SectionTitle>
                {md.cutting_area&&<Row label="Cutting Area" val={`${md.cutting_area} Sq.m`} mono bold color="#185FA5"/>}
                {md.rope_consumption&&<Row label="Rope Used" val={`${md.rope_consumption} m`} mono/>}
                {md.wastage_rope_weight&&<Row label="Wastage Weight" val={`${md.wastage_rope_weight} kg`} mono/>}
                <Row label="Rope Sold" val={md.rope_sold||'No'}/>
                {md.rope_sold==='Yes'&&<>
                  <Row label="Buyer" val={md.buyer_name} bold/>
                  <Row label="Amount" val={`₹${md.rope_sold_amount}`} mono color="#3B6D11"/>
                </>}
              </div>
              <div>
                <SectionTitle>Power — {md.power_source||'—'}</SectionTitle>
                {md.running_hours&&<Row label="Running Hours" val={`${md.running_hours}h ${md.running_minutes||0}m`} mono/>}
                {md.diesel_consumption&&<Row label="Diesel" val={`${md.diesel_consumption} L`} mono color="#854F0B"/>}
                {md.eb_start&&<>
                  <Row label="EB Start" val={md.eb_start} mono/>
                  <Row label="EB End" val={md.eb_end} mono/>
                  <Row label="Total Units" val={`${(parseFloat(md.eb_end||0)-parseFloat(md.eb_start||0)).toFixed(2)}`} mono bold/>
                </>}
              </div>
              <div>
                <SectionTitle>Operator</SectionTitle>
                {md.operator?.operator_name&&<Row label="Name" val={md.operator.operator_name} bold/>}
                {md.operator?.operator_role&&<Row label="Role" val={md.operator.operator_role}/>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderBladeCutting = () => (
  <div style={{padding:10,background:'#f8f9fc',borderRadius:6,border:'1px solid #e8eaf0'}}>
    <div style={{fontSize:12,fontWeight:700,color:'#185FA5',marginBottom:8}}>🔪 Blade Cutting — {d.blade_size} | {d.cutting_type}</div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:12 }}>
      <div>
        <SectionTitle>Work Details</SectionTitle>
        {d.blade_size&&<Row label="Blade Size" val={d.blade_size}/>}
        {d.cutting_type&&<Row label="Cutting Type" val={d.cutting_type}/>}
        {d.number_of_frames&&<Row label="No. of Frames" val={d.number_of_frames} mono bold/>}
        {d.running_hours&&<Row label="Running Hours" val={`${d.running_hours}h`} mono/>}
      </div>
      <div>
        <SectionTitle>Power — {d.power_source}</SectionTitle>
        {d.power_source==='Generator'&&<>
          {d.generator_hours&&<Row label="Hours" val={`${d.generator_hours}h ${d.generator_minutes||0}m`} mono/>}
          {d.diesel_consumption&&<Row label="Diesel" val={`${d.diesel_consumption} L`} mono color="#854F0B"/>}
        </>}
        {d.power_source==='Current'&&<>
          {d.eb_start&&<Row label="Start Reading" val={d.eb_start} mono/>}
          {d.eb_end&&<Row label="End Reading" val={d.eb_end} mono/>}
          {d.eb_start&&d.eb_end&&<Row label="Total Units" val={`${(parseFloat(d.eb_end)-parseFloat(d.eb_start)).toFixed(2)}`} mono bold color="#185FA5"/>}
        </>}
      </div>
      <div>
        <SectionTitle>Operator</SectionTitle>
        {d.operator?.operator_name&&<Row label="Name" val={d.operator.operator_name} bold/>}
        {d.operator?.operator_role&&<Row label="Role" val={d.operator.operator_role}/>}
      </div>
    </div>
  </div>
);

  const renderBlasting = () => (
    <div style={{ padding:10, background:'#fff5f5', borderRadius:6, border:'1px solid #fecaca' }}>
      <div style={{ fontSize:12, fontWeight:700, color:'#C0392B', marginBottom:8 }}>💥 Blasting — {d.blasting_type} {d.sub_type}</div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:12 }}>
        <div>
          <SectionTitle>Blasting Details</SectionTitle>
          {d.detonator_count&&<Row label="Detonators Used" val={d.detonator_count} mono bold/>}
          {d.thiri_used&&<Row label="Thiri/Fuse Wire" val={`${d.thiri_used} ft`} mono/>}
          {d.hole_feet&&<Row label="Hole Feet" val={`${d.hole_feet} ft`} mono bold color="#1e3a5f"/>}
        </div>
        <div>
          <SectionTitle>Safety Checklist</SectionTitle>
          {d.safety&&Object.entries(d.safety).map(([k,v])=>(
            <div key={k} style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, padding:'2px 0' }}>
              <span style={{ color:v?'#3B6D11':'#C0392B', fontWeight:700 }}>{v?'✓':'✗'}</span>
              <span style={{ color:'#555' }}>{k}</span>
            </div>
          ))}
        </div>
        <div>
          <SectionTitle>Operator</SectionTitle>
          {d.operator?.operator_name&&<Row label="Name" val={d.operator.operator_name} bold/>}
          {d.operator?.operator_role&&<Row label="Role" val={d.operator.operator_role}/>}
        </div>
      </div>
    </div>
  );

  const renderTransport = () => {
    const totalLoad = (parseFloat(d.trips||0) * parseFloat(d.load_per_trip||0)).toFixed(2);
    const fuelPerTrip = d.trips > 0 ? (parseFloat(d.fuel_consumption||0) / parseFloat(d.trips||1)).toFixed(2) : 0;
    return (
      <div style={{ padding:10, background:'#f8f9fc', borderRadius:6, border:'1px solid #e8eaf0' }}>
        <div style={{ fontSize:12, fontWeight:700, color:'#854F0B', marginBottom:8 }}>🚛 Transport — {d.vehicle_type}</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:12 }}>
          <div>
            <SectionTitle>Transport Details</SectionTitle>
            {d.material_type&&<Row label="Material" val={d.material_type}/>}
            {d.trips&&<Row label="No. of Trips" val={d.trips} mono bold/>}
            {d.load_per_trip&&<Row label="Load per Trip" val={`${d.load_per_trip} T`} mono/>}
            {totalLoad>0&&<Row label="Total Load" val={`${totalLoad} T`} mono bold color="#1e3a5f"/>}
          </div>
          <div>
            <SectionTitle>Fuel</SectionTitle>
            {d.fuel_consumption&&<Row label="Total Fuel" val={`${d.fuel_consumption} L`} mono bold color="#854F0B"/>}
            {fuelPerTrip>0&&<Row label="Fuel per Trip" val={`${fuelPerTrip} L`} mono color="#854F0B"/>}
            <SectionTitle>Route</SectionTitle>
            {d.from_location&&<Row label="From" val={d.from_location} bold/>}
            {d.to_location&&<Row label="To" val={d.to_location} bold/>}
          </div>
          <div>
            <SectionTitle>Driver</SectionTitle>
            {d.driver_name&&<Row label="Driver Name" val={d.driver_name} bold/>}
          </div>
        </div>
      </div>
    );
  };

  const renderGeneric = () => (
    <div style={{ padding:10, background:'#f8f9fc', borderRadius:6, border:'1px solid #e8eaf0' }}>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:12 }}>
        <div>
          <SectionTitle>Work Details</SectionTitle>
          {d.waste_source&&<Row label="Waste Source" val={d.waste_source}/>}
          {d.reason&&<Row label="Reason" val={d.reason}/>}
          {d.disposed&&<Row label="Disposed" val={d.disposed}/>}
          {d.disposed_to&&<Row label="Disposed To" val={d.disposed_to}/>}
          {d.description&&<Row label="Description" val={d.description}/>}
          {d.machine_used&&<Row label="Machine" val={d.machine_used}/>}
          {d.hours_spent&&<Row label="Hours Spent" val={`${d.hours_spent}h`} mono/>}
          {d.materials_used&&<Row label="Materials" val={d.materials_used}/>}
        </div>
        <div>
          <SectionTitle>Operator</SectionTitle>
          {d.operator?.operator_name&&<Row label="Name" val={d.operator.operator_name} bold/>}
          {d.operator?.operator_role&&<Row label="Role" val={d.operator.operator_role}/>}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ border:'1px solid #ddd', borderRadius:8, marginBottom:12, overflow:'hidden', breakInside:'avoid' }}>
      {/* Entry Header */}
      <div style={{ padding:'8px 14px', display:'flex', justifyContent:'space-between', alignItems:'center', background: wt==='DRILLING'?'#1e3a5f':wt==='WIRE_SAW'?'#185FA5':wt==='BLASTING'?'#C0392B':wt==='TRANSPORT'?'#854F0B':'#555' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.7)', background:'rgba(255,255,255,0.15)', padding:'2px 8px', borderRadius:20 }}>Entry {idx+1}</span>
          <span style={{ fontSize:13, fontWeight:700, color:'#fff' }}>{wtLabel[wt]||wt}</span>
          {wt==='DRILLING'&&d.category&&<span style={{ fontSize:11, color:'rgba(255,255,255,0.8)' }}>— {d.category} | {d.cut_type||d.bore_angle}</span>}
          {wt==='WIRE_SAW'&&d.cutting_type&&<span style={{ fontSize:11, color:'rgba(255,255,255,0.8)' }}>— {d.cutting_type}</span>}
        </div>
        <span style={{ fontSize:11, padding:'3px 10px', borderRadius:20, fontWeight:600, background:statusBg(entry.status||'IN_PROGRESS'), color:statusColor(entry.status||'IN_PROGRESS') }}>
          {entry.status==='COMPLETED'?'✓ Completed':entry.status==='ON_HOLD'?'⏸ On Hold':'● In Progress'}
        </span>
      </div>

      {/* Entry Body */}
      <div style={{ padding:'12px 14px' }}>
        {wt==='DRILLING'  && renderDrilling()}
        {wt==='WIRE_SAW'  && renderWireSaw()}
        {wt==='BLASTING'  && renderBlasting()}
        {wt==='TRANSPORT' && renderTransport()}
        {(wt==='OFFSET'||wt==='WASTE'||wt==='OTHER') && renderGeneric()}

        {/* Remarks */}
        {entry.remarks && entry.remarks !== 'None' && (
          <div style={{ marginTop:10, padding:'8px 12px', background:'#fffbeb', border:'1px solid #fde68a', borderRadius:6 }}>
            <div style={{ fontSize:9, color:'#854F0B', fontWeight:700, textTransform:'uppercase', letterSpacing:'1px', marginBottom:4 }}>📝 Remarks</div>
            <div style={{ fontSize:12, color:'#1e3a5f' }}>{entry.remarks}</div>
          </div>
        )}

        {/* Photo */}
        {entry.photo_url && (
          <div style={{ marginTop:10 }}>
            <div style={{ fontSize:9, color:'#666', fontWeight:700, textTransform:'uppercase', letterSpacing:'1px', marginBottom:6 }}>📷 Photo</div>
            <img
              src={entry.photo_url.startsWith('http') ? entry.photo_url : `http://127.0.0.1:8000${entry.photo_url}`}
              alt="Work entry"
              style={{ maxWidth:'100%', maxHeight:300, borderRadius:6, border:'1px solid #ddd' }}
              onError={e=>{ e.target.style.display='none'; }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function DailyReportPage() {
  const [filters, setFilters] = useState({
    date: new Date().toISOString().split('T')[0],
    direction: '', stage: '', shift: '',
  });
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [generated, setGenerated] = useState(false);
  const [reportNumber, setReportNumber] = useState('');
  const printRef = useRef();
  const setF = (k,v) => setFilters(f=>({...f,[k]:v}));

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/operations/sessions/?page_size=200&ordering=-started_date`);
      let all = res.data.results||[];
      all = all.filter(s => {
        if(s.started_date === filters.date) return true;
        try {
          const p = JSON.parse(s.notes||'{}');
          return (p.day_entries||[]).some(e => e.entry_date === filters.date);
        } catch { return false; }
      });
      if(filters.direction) all = all.filter(s => s.direction === filters.direction);
      if(filters.stage)     all = all.filter(s => s.production_stage === filters.stage);
      if(filters.shift)     all = all.filter(s => s.shift === filters.shift);
      setSessions(all);
      // Generate report number
      const d = new Date(filters.date);
      setReportNumber(`RPT/${d.getFullYear()}/${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}`);
      setGenerated(true);
    } finally { setLoading(false); }
  };

  // Collect all entries
  const allEntries = [];
  sessions.forEach(s => {
    try {
      const p = JSON.parse(s.notes||'{}');
      const entries = (p.day_entries||[]).filter(e => e.entry_date === filters.date || s.started_date === filters.date);
      entries.forEach(e => { if(e.work_type) allEntries.push(e); });
    } catch {}
  });

  const completed  = allEntries.filter(e=>e.status==='COMPLETED').length;
  const inProgress = allEntries.filter(e=>e.status==='IN_PROGRESS'||!e.status).length;
  const onHold     = allEntries.filter(e=>e.status==='ON_HOLD').length;

  // Diesel summary
  let totalDiesel = 0;
  allEntries.forEach(entry => {
    const d = entry.data || {};
    if(entry.work_type==='DRILLING') {
      Object.values(d.machine_data||{}).forEach(m => { totalDiesel += parseFloat(m.compressor?.fuel||0); });
    }
    if(entry.work_type==='WIRE_SAW') {
      Object.values(d.machine_data||{}).forEach(m => { totalDiesel += parseFloat(m.diesel_consumption||0); });
    }
    if(entry.work_type==='TRANSPORT') { totalDiesel += parseFloat(d.fuel_consumption||0); }
  });

  return (
    <Layout title="Daily Report" subtitle="Detailed work session report for print">
      {/* Print styles */}
      <style>{`
      @media print {
        .no-print { display: none !important; }
        .sidebar, .topbar, .hamburger { display: none !important; }
        .app-layout { display: block !important; }
        .main-content { 
        margin-left: 0 !important; 
        height: auto !important; 
        overflow: visible !important;
        display: block !important;
        }
        .page-content { 
          padding: 0 !important; 
          height: auto !important;
          overflow: visible !important;
        }
        .modal { max-height: none !important; overflow: visible !important; }
        @page { margin: 10mm; size: A4; }
      }
    `}</style>

      {/* Filters */}
      <div className="card no-print" style={{ marginBottom:16 }}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--brand)', borderBottom:'2px solid var(--border)', paddingBottom:8, marginBottom:16 }}>Report Filters</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:12, alignItems:'end' }}>
          <div className="form-group" style={{ flex:'1 1 140px' }}>
            <label className="form-label">Date *</label>
            <input className="form-input" type="date" value={filters.date} onChange={e=>setF('date',e.target.value)} max={new Date().toISOString().split('T')[0]}/>
          </div>
          <div className="form-group" style={{ flex:'1 1 140px' }}>
            <label className="form-label">Direction</label>
            <select className="form-input" value={filters.direction} onChange={e=>setF('direction',e.target.value)}>
              {DIRECTIONS.map(d=><option key={d.val} value={d.val}>{d.label}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ flex:'1 1 140px' }}>
            <label className="form-label">Stage</label>
            <select className="form-input" value={filters.stage} onChange={e=>setF('stage',e.target.value)}>
              {STAGES.map(s=><option key={s} value={s}>{s||'All Stages'}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ flex:'1 1 140px' }}>
            <label className="form-label">Shift</label>
            <select className="form-input" value={filters.shift} onChange={e=>setF('shift',e.target.value)}>
              {SHIFTS.map(s=><option key={s} value={s}>{s||'All Shifts'}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ flex:'0 0 auto' }}>
            <label className="form-label" style={{ opacity:0 }}>x</label>
            <button className="btn btn-primary" onClick={generate} disabled={loading} style={{ whiteSpace:'nowrap' }}>
              {loading?'Loading...':'Generate Report'}
            </button>
          </div>
        </div>
      </div>

      {generated && (
        <div ref={printRef}>
          {/* Print button */}
          <div className="no-print" style={{ display:'flex', justifyContent:'flex-end', marginBottom:16, gap:8 }}>
            <button className="btn btn-secondary btn-sm" onClick={()=>{
              const reportData = JSON.stringify({
                sessions, filters, reportNumber, allEntries
              });
              sessionStorage.setItem('printReport', reportData);
              // Small delay to ensure storage is set
              setTimeout(()=>{
                window.open('/print/daily-report', '_blank');
              }, 100);
            }}>🖨 Print / Save as PDF</button>
          </div>

          {/* ── PRINTABLE REPORT ── */}
          <div id="daily-report-content" className="print-content" style={{ background:'#fff', padding:8, border:'1px solid #ddd', borderRadius:8 }}>

            {/* Company Header */}
            <CompanyHeader
              reportTitle="Daily Work Report"
              reportDate={formatDate(filters.date)}
              reportNumber={reportNumber}
            />

            {/* Summary Bar */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(90px, 1fr))', gap:0, border:'1px solid #ddd', borderRadius:6, marginBottom:10 }}>
              {[
                ['Total Sessions', sessions.length, '#1e3a5f'],
                ['Work Entries', allEntries.length, '#185FA5'],
                ['Completed', completed, '#3B6D11'],
                ['In Progress', inProgress, '#854F0B'],
                ['Total Diesel', `${totalDiesel.toFixed(1)} L`, '#C0392B'],
              ].map(([l,v,c],i)=>(
                <div key={i} style={{ padding:'3px 6px', textAlign:'center', background:i%2===0?'#f8f9fc':'#fff', borderRight:i<4?'1px solid #ddd':'none' }}>
                  <div style={{ fontSize:12, fontWeight:700, color:c, fontFamily:'DM Mono,monospace' }}>{v}</div>
                  <div style={{ fontSize:9, color:'#666', textTransform:'uppercase', letterSpacing:'0.5px', marginTop:2 }}>{l}</div>
                </div>
              ))}
            </div>

            {/* No sessions */}
            {sessions.length===0 && (
              <div style={{ textAlign:'center', padding:40, color:'#999', border:'1px dashed #ddd', borderRadius:8 }}>
                No work sessions found for the selected filters.
              </div>
            )}

            {/* Session Details */}
            {sessions.map((session, si) => {
              let parsedNotes = {};
              let dayEntries = [];
              try {
                parsedNotes = JSON.parse(session.notes||'{}');
                dayEntries = (parsedNotes.day_entries||[]).filter(e =>
                  e.entry_date === filters.date || session.started_date === filters.date
                );
              } catch {}
              if(dayEntries.length===0 && session.started_date===filters.date) {
                dayEntries = parsedNotes.day_entries||[];
              }
              const lbh = parsedNotes.lbh;

              return (
                <div key={si} style={{ marginBottom:24, breakInside:'avoid' }}>
                  {/* Session Header */}
                  <div style={{ background:'#1e3a5f', padding:'10px 16px', borderRadius:'8px 8px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <span style={{ color:'#fff', fontWeight:700, fontSize:14, fontFamily:'DM Mono,monospace' }}>{session.session_number}</span>
                      <span style={{ color:'rgba(255,255,255,0.6)', fontSize:12, marginLeft:14 }}>{session.shift} Shift</span>
                    </div>
                    <span style={{ fontSize:11, padding:'3px 12px', borderRadius:20, fontWeight:600, background:statusBg(session.status||'IN_PROGRESS'), color:statusColor(session.status||'IN_PROGRESS') }}>
                      Overall: {(session.status||'IN_PROGRESS').replace('_',' ')}
                    </span>
                  </div>

                  {/* Supervisor + Session Info */}
                  <div style={{ border:'1px solid #ddd', borderTop:'none', padding:14, borderRadius:'0 0 0 0' }}>
                    <SupervisorBox session={session}/>

                    {/* LBH */}
                    {lbh&&lbh.height&&(
                      <div style={{ background:'#E6F1FB', border:'1px solid #B5D4F4', borderRadius:6, padding:'8px 14px', marginBottom:12, display:'flex', gap:24 }}>
                        <span style={{ fontSize:12, color:'#0C447C', fontWeight:500 }}>
                          📐 LBH: <strong>{lbh.length||'10.5'} × {lbh.breadth||'8.8'} × {lbh.height} m</strong>
                        </span>
                        <span style={{ fontSize:12, color:'#0C447C' }}>
                          Volume: <strong>{((parseFloat(lbh.length||10.5))*(parseFloat(lbh.breadth||8.8))*(parseFloat(lbh.height||0))).toFixed(3)} m³</strong>
                        </span>
                      </div>
                    )}

                    {/* Work Entries */}
                    {dayEntries.filter(e=>e.work_type).length===0 ? (
                      <div style={{ textAlign:'center', padding:20, color:'#999', fontSize:13 }}>No work entries for this date.</div>
                    ) : (
                      dayEntries.filter(e=>e.work_type).map((entry, ei)=>(
                        <WorkEntryDetail key={ei} entry={entry} idx={ei}/>
                      ))
                    )}
                  </div>

                  {/* Footer per session */}
                  {parsedNotes.user_notes&&(
                    <div style={{ border:'1px solid #ddd', borderTop:'none', padding:'8px 14px', background:'#fffbeb', borderRadius:'0 0 8px 8px' }}>
                      <span style={{ fontSize:10, fontWeight:700, color:'#854F0B', textTransform:'uppercase' }}>Session Notes: </span>
                      <span style={{ fontSize:12, color:'#333' }}>{parsedNotes.user_notes}</span>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Report Footer */}
            <div style={{ borderTop:'2px solid #1e3a5f', paddingTop:12, marginTop:20, display:'flex', justifyContent:'space-between', fontSize:11, color:'#666' }}>
              <span>TYCHE ABODES PRIVATE LIMITED — Confidential</span>
              <span>Report: {reportNumber} | Generated: {new Date().toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}